import type {
  GemInclusionParams,
  GemMaterialParams,
  GroupGemInclusionState,
  GroupInfo,
  GroupMaterialParams,
  MaterialExtensionConfig,
  MaterialKind,
  MaterialPresetItem,
  MetalMaterialParams,
} from "../../../shared/api-contract";
import { LOOK_FIELDS, lookField, sanitizeLookValue, type LookCategory } from "../../../shared/lookFields";
import type { EditorStore } from "../../../state/editor/EditorStore";
import type { BindingCtx, DomainRead } from "../../../state/editor/types";
import type { SliderRow } from "../inspector/schema";
import { isDmatMark, markKind } from "./markKind";
import { CLARITY_GRADES, CLARITY_ROW, clarityOfState, FLAWLESS_PAYLOAD, type ClarityId } from "./clarity";
import { LEVELS, stepperById, type StepperDef, type StepTarget } from "./macros";
import { LOOK_TARGETS, METAL_EXT_SLIDERS, resolveRow, type LookTarget } from "./targets";

/**
 * The one place Basic mode and AI Look Match change the scene.
 *
 * - Writes go through the Advanced rows' own bindings (row targets) or the
 *   same material RPCs those bindings call, once per part (material targets) —
 *   see targets.ts. So every change shows up on the Advanced sliders.
 * - Everything runs on ONE serial queue: two quick presses can never read the
 *   same "current" value and both add a step to it.
 * - Every step is recorded for Undo, as the values it replaced.
 * - The first change after a model loads snapshots every field; Reset writes
 *   that snapshot back ("how it looked before I started tuning").
 */

export type LookValue = number | boolean | string | null;

/** Which parts a material field reaches: every part of that kind, or one. */
export interface BasicScope {
  gem: "all" | string;
  metal: "all" | string;
}
export const ALL_PARTS: BasicScope = { gem: "all", metal: "all" };

export interface LookChange {
  key: string;
  value: LookValue;
}

interface Write {
  key: string;
  value: LookValue;
  /** Material fields: the one part this write targets. */
  groupId?: string;
}

interface UndoEntry {
  label: string;
  /** Values to write back on Undo. */
  restore: Write[];
  /** Values to write again on Redo. */
  applied: Write[];
  /**
   * Steps that are more than look-field writes (a named metal preset, a part
   * reclassified): their own undo/redo, run INSTEAD of the write lists.
   */
  custom?: { undo: () => Promise<void>; redo: () => Promise<void> };
  /** `recordExternal` coalescing: the gesture this entry belongs to, and when it last moved. */
  key?: string;
  at?: number;
}

/**
 * A material the viewer applied to one part through us, as far as we know it
 * — the viewer cannot report it back, so this is what Undo re-applies.
 *
 * Three shapes, routed by `putPreset`:
 * - a named metal or stone: `presetId` (+ `finishId` for metals) →
 *   applyMetalPresetToGroup / applyGemPresetToGroup;
 * - a material FILE (a curated .pmat/.dmat, or one the workspace added, which
 *   travels as a data: URL): `url` → applyPmatUrlToGroup / applyDmatUrlToGroup;
 * - a built-in ceramic / pearl / enamel: `category` other than metal or gem →
 *   applyMaterialPresetToGroup, the only RPC that knows those ids.
 */
export interface PresetMark {
  /** The named preset, or the library item's id (what the panel rings as chosen). */
  presetId: string;
  /** Named metals only. */
  finishId?: string;
  /** A material file; wins over `presetId` for what gets applied. */
  url?: string;
  /** The library category it came from: usesDmat's tiebreak for a URL with no
   *  extension, and what marks a built-in physical preset. */
  category?: MaterialPresetItem["category"];
  /** Learned on apply: the viewer showed this .pmat to DECLARE a DiamondMaterial,
   *  which it applies as a diamond whatever the file is called. */
  dmat?: boolean;
}

/**
 * Where the Undo history stood at one moment. `rewindTo` comes back to it
 * exactly — the steps taken since are undone and dropped, and Undo and Redo
 * hold what they held then. Preview as customer is why: a shopper's
 * clicks must leave no trace in the designer's history.
 */
export interface HistoryMark {
  readonly undo: readonly UndoEntry[];
  readonly redo: readonly UndoEntry[];
  readonly lastStep: AppliedStep | null;
  /** The history's generation: a new model starts a new one, and a mark from
   *  an old one has nothing left to rewind. */
  readonly generation: number;
}

export interface AppliedStep {
  label: string;
  /** Plain words for the status line: "Facets: level 3 → 4 · moved 3 settings". */
  summary?: string;
  /** Human caption: "Gamma 1.00→1.10 · Bounces 5→6". */
  caption: string;
  /** Advanced rows this step moved, `tab.section.row`. */
  rows: string[];
  at: number;
  /** Which Basic control made it, if one did. */
  controlId?: string;
}

const MATERIAL_KIND: Record<LookTarget["kind"], MaterialKind | null> = {
  row: null,
  gem: "gem",
  gemColor: "gem",
  metal: "metal",
  metalColor: "metal",
  metalExt: "metal",
};

function stepDecimals(step: number): number {
  return step >= 1 ? 0 : Math.min(6, Math.ceil(-Math.log10(step) - 1e-9));
}

// ── levels (pure; scripts/check-basic-macros.ts proves the round trip) ──────

/** The Advanced slider's own step for a look field, used to quantise writes. */
export function sliderStep(key: string): number | null {
  const t = LOOK_TARGETS[key];
  if (!t) return null;
  if (t.kind === "metalExt") return METAL_EXT_SLIDERS[t.field]?.step ?? null;
  const r = resolveRow(t.row);
  return r && r.row.kind === "slider" ? (r.row as SliderRow).step : null;
}

/**
 * Clamp a value to its field's good-looking band and snap it to the Advanced
 * slider's grid, so every value Basic writes is one the slider shows exactly.
 * Integer fields round.
 */
export function clampQuant(key: string, v: number): number {
  const f = lookField(key);
  if (!f) return v;
  let x = Math.min(f.hi ?? v, Math.max(f.lo ?? v, v));
  const step = sliderStep(key);
  if (step) x = Number((Math.round(x / step) * step).toFixed(stepDecimals(step)));
  if (f.integer) x = Math.round(x);
  return x;
}

/** The band a stepper target spans: its own `lo`/`hi`, else the field's. */
export function targetBand(t: StepTarget): { lo: number; hi: number } | null {
  const f = lookField(t.field);
  const lo = t.lo ?? f?.lo;
  const hi = t.hi ?? f?.hi;
  return lo === undefined || hi === undefined ? null : { lo, hi };
}

/** The value a target takes at level `n` (1..LEVELS), quantised. */
export function levelToValue(t: StepTarget, n: number): number | null {
  const band = targetBand(t);
  if (!band) return null;
  const level = Math.min(LEVELS, Math.max(1, Math.round(n)));
  let pos = (level - 1) / (LEVELS - 1);
  if (t.invert) pos = 1 - pos;
  return clampQuant(t.field, band.lo + pos * (band.hi - band.lo));
}

/** The level (1..LEVELS) a value sits at on a target's band — nearest, clamped. */
export function valueToLevel(t: StepTarget, v: number): number | null {
  const band = targetBand(t);
  if (!band || band.hi === band.lo) return null;
  let pos = (v - band.lo) / (band.hi - band.lo);
  if (t.invert) pos = 1 - pos;
  return Math.min(LEVELS, Math.max(1, Math.round(pos * (LEVELS - 1)) + 1));
}

const sameValue = (a: LookValue, b: LookValue) =>
  a === b || (typeof a === "number" && typeof b === "number" && Math.abs(a - b) < 1e-9);

/** A clarity grade's inclusion state as the setter takes it. */
function inclusionPayload(st: GroupGemInclusionState | null): GemInclusionParams {
  if (!st) return { ...FLAWLESS_PAYLOAD };
  const { groupId: _groupId, ...params } = st;
  void _groupId;
  return params;
}

function toHex(c: [number, number, number]): string {
  const h = (x: number) => Math.round(Math.min(1, Math.max(0, x)) * 255).toString(16).padStart(2, "0");
  return `#${h(c[0])}${h(c[1])}${h(c[2])}`;
}

function fromHex(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255) as [number, number, number];
}

function fmt(v: LookValue): string {
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(Math.abs(v) < 0.1 ? 3 : 2);
  if (typeof v === "boolean") return v ? "on" : "off";
  return v ?? "none";
}

export class BasicEngine {
  private queue: Promise<unknown> = Promise.resolve();
  private undoStack: UndoEntry[] = [];
  private redoStack: UndoEntry[] = [];
  /** Compare: the values in force before the loaded look was shown. */
  private previewSnap: Map<string, LookValue> | null = null;
  /** Named presets applied through us, per part (the viewer cannot report them). */
  private presetOf = new Map<string, PresetMark>();
  private metalColors: Record<string, number> = {};
  private baseline: Map<string, LookValue> | null = null;
  /** Bumped whenever the history is thrown away (a new model). */
  private generation = 0;
  private mats = new Map<string, GroupMaterialParams>();
  /** Each metal part's shading extensions (the glitter flakes live here). */
  private exts = new Map<string, MaterialExtensionConfig>();
  private matsLoad: Promise<void> | null = null;
  /** Each stone's inclusion state, for the Clarity row. Read only once the
   *  panel says the viewer has the v62 inclusion model (`setClarityEnabled`). */
  private inclusions = new Map<string, GroupGemInclusionState | null>();
  private inclusionsLoad: Promise<void> | null = null;
  private clarityOn = false;
  /** Each stone's inclusions before Basic first changed them — what Reset
   *  puts back (the look-field baseline cannot hold them). */
  private inclusionBaseline = new Map<string, GroupGemInclusionState | null>();
  private listeners = new Set<() => void>();
  private ctx: BindingCtx | null = null;
  private version = 0;
  lastStep: AppliedStep | null = null;
  busy = false;

  constructor(private store: EditorStore) {
    // A new model means new part ids: the per-part cache, the baseline and the
    // undo history all describe parts that no longer exist.
    store.subscribe("models", () => {
      this.generation++;
      this.baseline = null;
      this.undoStack = [];
      this.redoStack = [];
      this.previewSnap = null;
      this.presetOf.clear();
      this.inclusionBaseline.clear();
      this.lastStep = null;
      this.invalidateMaterials();
    });
    // Parts added/removed, or a preset applied from the left rail — re-read.
    store.subscribe("groups", () => this.invalidateMaterials());
    // An Advanced edit to the selected part: fold it into the cache so the
    // Basic meters never show a value the Advanced slider disagrees with.
    store.subscribe("material", () => {
      const id = store.getState("selection") as string | null;
      const m = store.getState("material") as GroupMaterialParams | null;
      if (id && m) {
        this.mats.set(id, m);
        this.emit();
      }
    });
  }

  /** Metal preset colours (`Catalogs.metalPresets`), for undoing a preset whose
   *  predecessor the viewer chose itself: the nearest named metal is re-applied
   *  before the exact values are written back. */
  setMetalColors(colors: Record<string, number>) {
    this.metalColors = colors;
  }

  /** The provider rebuilds ctx on selection change; the panel keeps us current. */
  setCtx(ctx: BindingCtx) {
    this.ctx = ctx;
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Bumped on every change the UI should re-read. */
  getVersion(): number {
    return this.version;
  }

  private emit() {
    this.version++;
    for (const fn of this.listeners) fn();
  }

  private get ring() {
    if (!this.ctx) throw new Error("BasicEngine used before its binding ctx was set");
    return this.ctx.ring;
  }

  private readDomain: DomainRead = (d) => this.store.getState(d);

  // ── parts ────────────────────────────────────────────────────────────────

  groups(kind: MaterialKind): GroupInfo[] {
    return ((this.store.getState("groups") as GroupInfo[] | undefined) ?? []).filter((g) => g.kind === kind);
  }

  private scopeGroups(kind: MaterialKind, scope: BasicScope): GroupInfo[] {
    const all = this.groups(kind);
    const pick = scope[kind];
    if (pick === "all") return all;
    const one = all.find((g) => g.id === pick);
    return one ? [one] : all;
  }

  private invalidateMaterials() {
    this.matsLoad = null;
    void this.ensureMaterials();
    this.inclusionsLoad = null;
    if (this.clarityOn) void this.ensureInclusions();
  }

  /**
   * Re-read every part from the viewer. Advanced's custom sections (material
   * extensions, gem inclusions) write the viewer directly and land in no
   * slice, so the panel calls this when it comes back into view.
   */
  refreshParts() {
    this.invalidateMaterials();
  }

  /** Load every part's material params once; later calls reuse the promise. */
  ensureMaterials(): Promise<void> {
    // Not cached until there is a ctx to load with — otherwise an early call
    // would pin an empty result for the rest of the session.
    if (!this.ctx) return Promise.resolve();
    if (!this.matsLoad) {
      const ids = [...this.groups("gem"), ...this.groups("metal")].map((g) => g.id);
      const metalIds = this.groups("metal").map((g) => g.id);
      this.matsLoad = (async () => {
        const [results, exts] = await Promise.all([
          Promise.all(ids.map((id) => this.ring.getGroupMaterialParams(id).catch(() => null))),
          Promise.all(metalIds.map((id) => this.ring.getGroupMaterialExtensions(id).catch(() => null))),
        ]);
        ids.forEach((id, i) => {
          const r = results[i];
          if (r) this.mats.set(id, r);
        });
        metalIds.forEach((id, i) => {
          const e = exts[i];
          if (e) this.exts.set(id, e);
        });
        this.emit();
      })();
    }
    return this.matsLoad;
  }

  /**
   * The Clarity row exists only on a viewer with the v62 inclusion model —
   * the panel passes its caps here. Off, the engine never asks the viewer
   * about inclusions (an older viewer may not answer).
   */
  setClarityEnabled(on: boolean) {
    if (on === this.clarityOn) return;
    this.clarityOn = on;
    this.inclusionsLoad = null;
    if (on) void this.ensureInclusions();
  }

  /** Every stone's inclusion state, read once; NOT part of the write queue, so
   *  a slow read can never hold up a press. */
  private ensureInclusions(): Promise<void> {
    if (!this.ctx || !this.clarityOn) return Promise.resolve();
    if (!this.inclusionsLoad) {
      const ids = this.groups("gem").map((g) => g.id);
      this.inclusionsLoad = this.readInclusions(ids);
    }
    return this.inclusionsLoad;
  }

  private async readInclusions(ids: string[]) {
    const states = await Promise.all(ids.map((id) => this.ring.getGroupGemInclusions(id).catch(() => null)));
    ids.forEach((id, i) => this.inclusions.set(id, states[i]));
    this.emit();
  }

  // ── reading ──────────────────────────────────────────────────────────────

  private target(key: string): LookTarget {
    const t = LOOK_TARGETS[key];
    if (!t) throw new Error(`No target for look field "${key}"`);
    return t;
  }

  private readRow(key: string): LookValue {
    const r = resolveRow(this.target(key).row);
    if (!r || !("binding" in r.row)) return null;
    if (!this.ctx) return null;
    const binding = r.row.binding as { domain: Parameters<EditorStore["getState"]>[0]; get: (read: DomainRead, ctx: BindingCtx) => unknown };
    // ⚠️ A slice that has not been seeded yet is `undefined`, and the row
    // getters dereference it (`s.ssr.intensity`). The inspector guards this
    // with its `pending` check; a Basic panel that opens on first paint — its
    // default — reaches here before the eager seeds land, and a throw from a
    // render tears down the studio's error boundary, iframe and all, which
    // leaves every RPC hanging. Not seeded means "no value yet".
    if (this.store.getState(binding.domain) === undefined) return null;
    try {
      return binding.get(this.readDomain, this.ctx) as LookValue;
    } catch {
      return null;
    }
  }

  private readPart(key: string, groupId: string): LookValue {
    const t = this.target(key);
    const m = this.mats.get(groupId);
    if (!m) return null;
    if (t.kind === "gem" && m.kind === "gem") return (m as GemMaterialParams)[t.uniform] as number;
    if (t.kind === "gemColor" && m.kind === "gem") return toHex((m as GemMaterialParams).color);
    if (t.kind === "metal" && m.kind === "metal") return (m as MetalMaterialParams)[t.param] as number;
    if (t.kind === "metalColor" && m.kind === "metal") return (m as MetalMaterialParams).color.toLowerCase();
    if (t.kind === "metalExt" && m.kind === "metal") return this.exts.get(groupId)?.[t.field] ?? null;
    return null;
  }

  /**
   * Current value of a field. Material numbers are the MEAN over the parts in
   * scope (parts can differ; the meter shows the typical one); colours are the
   * first part's.
   */
  read(key: string, scope: BasicScope = ALL_PARTS): LookValue {
    const kind = MATERIAL_KIND[this.target(key).kind];
    if (!kind) return this.readRow(key);
    const vals = this.scopeGroups(kind, scope)
      .map((g) => this.readPart(key, g.id))
      .filter((v) => v !== null);
    if (vals.length === 0) return null;
    if (typeof vals[0] !== "number") return vals[0];
    return (vals as number[]).reduce((a, b) => a + b, 0) / vals.length;
  }

  /** Whether the field has anything to act on (a gem field with no gems does not). */
  available(key: string): boolean {
    const kind = MATERIAL_KIND[this.target(key).kind];
    return kind ? this.groups(kind).length > 0 : true;
  }

  // ── levels (for the UI) ──────────────────────────────────────────────────

  private ensureOff(ctrl: StepperDef, scope: BasicScope): boolean {
    return (ctrl.ensure ?? []).some((k) => this.read(k, scope) === false);
  }

  /**
   * A stepper's level, 1..10, read from its FIRST target (the mean over the
   * parts in scope for a material field). 0 while a switch it needs is off;
   * null when there is nothing to read yet.
   */
  levelOf(ctrl: StepperDef, scope: BasicScope = ALL_PARTS): number | null {
    const p = ctrl.targets[0];
    if (!this.available(p.field)) return null;
    const v = this.read(p.field, scope);
    if (typeof v !== "number") return null;
    if (this.ensureOff(ctrl, scope)) return 0;
    return valueToLevel(p, v);
  }

  /** Can a press in `dir` change anything? */
  canStep(ctrl: StepperDef, dir: 1 | -1, scope: BasicScope = ALL_PARTS): boolean {
    const level = this.levelOf(ctrl, scope);
    if (level === null) return false;
    if (level === 0) return dir === 1;
    return dir === 1 ? level < LEVELS : level > 1;
  }

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  /** How many steps Undo can take back right now. */
  undoDepth(): number {
    return this.undoStack.length;
  }

  /** The history as it stands — hand it to `rewindTo` to come back here. */
  historyMark(): HistoryMark {
    return { undo: [...this.undoStack], redo: [...this.redoStack], lastStep: this.lastStep, generation: this.generation };
  }

  /** `historyMark`, taken once every step already queued has landed. */
  markHistory(): Promise<HistoryMark> {
    return this.enqueue(async () => this.historyMark());
  }

  /**
   * Undo every step taken since `mark`, newest first, then put Undo and Redo
   * back exactly as they were at the mark — so the steps leave no entry
   * behind, and Redo still offers what it offered then. Resolves false when a
   * new model replaced the history in between (there is nothing to rewind).
   */
  rewindTo(mark: HistoryMark): Promise<boolean> {
    return this.enqueue(async () => {
      if (mark.generation !== this.generation) return false;
      const kept = new Set(mark.undo);
      const since = this.undoStack.filter((e) => !kept.has(e));
      for (const entry of since.reverse()) {
        if (entry.custom) await entry.custom.undo();
        else await this.applyWrites(entry.restore);
      }
      this.undoStack = [...mark.undo];
      this.redoStack = [...mark.redo];
      this.lastStep = mark.lastStep;
      return true;
    });
  }

  /** What Undo / Redo would do, for their tooltips ("Undo Glow · Intensity"). */
  undoLabel(): string | null {
    return this.undoStack[this.undoStack.length - 1]?.label ?? null;
  }

  redoLabel(): string | null {
    return this.redoStack[this.redoStack.length - 1]?.label ?? null;
  }

  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /** Has anything changed since the model loaded (so Compare has a "before")? */
  hasChanges(): boolean {
    return this.baseline !== null && this.undoStack.length > 0;
  }

  get previewing(): boolean {
    return this.previewSnap !== null;
  }

  /** The named preset last applied to a part through Basic mode, if any. */
  presetFor(groupId: string): PresetMark | undefined {
    return this.presetOf.get(groupId);
  }

  // ── writing ──────────────────────────────────────────────────────────────

  private enqueue<T>(job: () => Promise<T>): Promise<T> {
    const run = this.queue.then(async () => {
      this.busy = true;
      this.emit();
      try {
        await this.ensureMaterials();
        if (!this.baseline) this.baseline = this.snapshotAll();
        return await job();
      } finally {
        this.busy = false;
        this.emit();
      }
    });
    this.queue = run.catch(() => undefined);
    return run;
  }

  /** Every field, per part for material fields, keyed `key` or `key@groupId`. */
  private snapshotAll(): Map<string, LookValue> {
    const snap = new Map<string, LookValue>();
    for (const f of LOOK_FIELDS) {
      const kind = MATERIAL_KIND[this.target(f.key).kind];
      if (!kind) snap.set(f.key, this.readRow(f.key));
      else for (const g of this.groups(kind)) snap.set(`${f.key}@${g.id}`, this.readPart(f.key, g.id));
    }
    return snap;
  }

  /** Apply writes; returns the restore list (old values) for Undo. */
  private async applyWrites(writes: Write[]): Promise<Write[]> {
    const restore: Write[] = [];
    const rowJobs: Promise<void>[] = [];
    const partJobs: Promise<unknown>[] = [];
    let touchedSelected = false;
    const selected = this.store.getState("selection") as string | null;

    for (const w of writes) {
      const t = this.target(w.key);
      if (t.kind === "row") {
        const r = resolveRow(t.row);
        if (!r || !("binding" in r.row) || !this.ctx) continue;
        restore.push({ key: w.key, value: this.readRow(w.key) });
        const binding = r.row.binding as { domain: Parameters<EditorStore["mutate"]>[0]; set: (v: unknown, c: BindingCtx) => Parameters<EditorStore["mutate"]>[1] };
        rowJobs.push(this.store.mutate(binding.domain, binding.set(w.value, this.ctx)));
        continue;
      }
      if (!w.groupId) continue;
      const id = w.groupId;
      restore.push({ key: w.key, value: this.readPart(w.key, id), groupId: id });
      if (id === selected) touchedSelected = true;
      const m = this.mats.get(id);
      if (t.kind === "gem" && typeof w.value === "number") {
        partJobs.push(this.ring.setGroupGemUniform(id, t.uniform, w.value));
        if (m?.kind === "gem") this.mats.set(id, { ...m, [t.uniform]: w.value });
      } else if (t.kind === "gemColor" && typeof w.value === "string") {
        const [r, g, b] = fromHex(w.value);
        partJobs.push(this.ring.setGroupGemVec3Component(id, "color", "x", r));
        partJobs.push(this.ring.setGroupGemVec3Component(id, "color", "y", g));
        partJobs.push(this.ring.setGroupGemVec3Component(id, "color", "z", b));
        if (m?.kind === "gem") this.mats.set(id, { ...m, color: [r, g, b] });
      } else if (t.kind === "metal" && typeof w.value === "number") {
        partJobs.push(this.ring.setGroupMetalParam(id, t.param, w.value));
        if (m?.kind === "metal") this.mats.set(id, { ...m, [t.param]: w.value });
      } else if (t.kind === "metalColor" && typeof w.value === "string") {
        partJobs.push(this.ring.setGroupMetalColor(id, w.value));
        if (m?.kind === "metal") this.mats.set(id, { ...m, color: w.value });
      } else if (t.kind === "metalExt" && w.value !== null) {
        const patch = { [t.field]: w.value } as Partial<MaterialExtensionConfig>;
        const ext = this.exts.get(id);
        if (ext) this.exts.set(id, { ...ext, ...patch });
        partJobs.push(
          Promise.resolve(this.ring.setGroupMaterialExtensions(id, patch)).then((full) => {
            if (full) this.exts.set(id, full);
          }),
        );
      }
    }
    await Promise.all([...rowJobs, ...partJobs.map((p) => Promise.resolve(p).catch(() => undefined))]);
    // The selected part's Advanced sliders read the `material` slice — re-read
    // it, or they would keep showing the value from before this step.
    if (touchedSelected) await this.store.refresh("material");
    return restore;
  }

  /** Expand look changes into per-part writes for material fields. */
  private expand(changes: LookChange[], scope: BasicScope): Write[] {
    const out: Write[] = [];
    for (const c of changes) {
      const kind = MATERIAL_KIND[this.target(c.key).kind];
      if (!kind) out.push({ key: c.key, value: c.value });
      else for (const g of this.scopeGroups(kind, scope)) out.push({ key: c.key, value: c.value, groupId: g.id });
    }
    return out;
  }

  /** One "Label old→new" per field (a material field shows its first part). */
  private caption(before: Write[], after: Write[]): { caption: string; rows: string[] } {
    const was = new Map<string, LookValue>();
    for (const w of before) if (!was.has(w.key)) was.set(w.key, w.value);
    const parts: string[] = [];
    const rows: string[] = [];
    const seen = new Set<string>();
    for (const w of after) {
      if (seen.has(w.key) || !was.has(w.key)) continue;
      seen.add(w.key);
      rows.push(this.target(w.key).row);
      parts.push(`${lookField(w.key)?.label ?? w.key} ${fmt(was.get(w.key) ?? null)}→${fmt(w.value)}`);
    }
    return { caption: parts.join(" · "), rows };
  }

  private record(label: string, restore: Write[], applied: Write[], controlId?: string, summary?: string) {
    if (restore.length === 0) return;
    this.push({ label, restore, applied });
    const { caption, rows } = this.caption(restore, applied);
    const moved = new Set(applied.map((w) => w.key)).size;
    this.lastStep = {
      label,
      summary: summary ?? `${label} · moved ${moved} setting${moved === 1 ? "" : "s"}`,
      caption,
      rows,
      at: Date.now(),
      controlId,
    };
  }

  private push(entry: UndoEntry) {
    this.undoStack.push(entry);
    if (this.undoStack.length > 30) this.undoStack.shift();
    // A new change forks history: what Redo would have brought back is gone.
    this.redoStack = [];
  }

  /**
   * An edit made OUTSIDE Basic — an Advanced row, a section preset — joins
   * this same history, so the top bar's Undo and ⌘Z cover every change in the
   * studio, not only the ones Basic made. The edit has already been applied;
   * `undo` / `redo` put it back and forward.
   *
   * `key` coalesces a gesture: a slider drag streams a write per frame, and a
   * second record with the same key while that entry is still the newest (and
   * moved within `mergeMs`) only moves its redo target and keeps its undo — one
   * drag, one step. `part` names the material part the edit wrote, so its
   * cached params (and the selected part's panel) are re-read after an undo.
   */
  recordExternal(
    e: { label: string; key?: string; part?: string; undo: () => Promise<void>; redo: () => Promise<void> },
    mergeMs = 1200,
  ) {
    const now = Date.now();
    const settle = (run: () => Promise<void>) => async () => {
      await run();
      if (e.part) await this.reloadParts([e.part]);
    };
    const top = this.undoStack[this.undoStack.length - 1];
    if (e.key && top?.key === e.key && top.custom && this.redoStack.length === 0 && now - (top.at ?? 0) < mergeMs) {
      top.custom.redo = settle(e.redo);
      top.at = now;
      this.emit();
      return;
    }
    this.push({ label: e.label, restore: [], applied: [], custom: { undo: settle(e.undo), redo: settle(e.redo) }, key: e.key, at: now });
    this.emit();
  }

  /**
   * Put a stepper at level `n` (1..10): EVERY target goes to its value for
   * that level, on every part in scope, as ONE Undo step. Any switch the
   * control needs is turned on in the same step.
   */
  setLevel(control: string | StepperDef, n: number, scope: BasicScope = ALL_PARTS): Promise<AppliedStep | null> {
    return this.enqueue(async () => this.applyLevel(this.stepper(control), n, scope));
  }

  /** One level toward "less" (−1) or "more" (+1). From "Off", "more" is level 1. */
  press(control: string | StepperDef, dir: 1 | -1, scope: BasicScope = ALL_PARTS): Promise<AppliedStep | null> {
    return this.enqueue(async () => {
      const ctrl = this.stepper(control);
      const level = this.levelOf(ctrl, scope);
      if (level === null) return null;
      if (level === 0) return dir === 1 ? this.applyLevel(ctrl, 1, scope) : null;
      const next = level + dir;
      if (next < 1 || next > LEVELS) return null;
      return this.applyLevel(ctrl, next, scope);
    });
  }

  private stepper(control: string | StepperDef): StepperDef {
    const ctrl = typeof control === "string" ? stepperById(control) : control;
    if (!ctrl) throw new Error(`Unknown stepper "${String(control)}"`);
    return ctrl;
  }

  /** Inside the queue: write a level, skipping values already in place. */
  private async applyLevel(ctrl: StepperDef, n: number, scope: BasicScope): Promise<AppliedStep | null> {
    const level = Math.min(LEVELS, Math.max(1, Math.round(n)));
    const changes: LookChange[] = (ctrl.ensure ?? []).map((key) => ({ key, value: true }));
    for (const t of ctrl.targets) {
      if (!this.available(t.field)) continue;
      const v = levelToValue(t, level);
      if (v !== null) changes.push({ key: t.field, value: v });
    }
    const writes = this.expand(changes, scope).filter((w) => !sameValue(this.current(w), w.value));
    if (writes.length === 0) return null;
    const before = this.levelOf(ctrl, scope);
    const restore = await this.applyWrites(writes);
    const after = this.levelOf(ctrl, scope);
    const moved = new Set(writes.map((w) => w.key)).size;
    const from = before === 0 ? "off" : String(before ?? "?");
    this.record(
      `${ctrl.label} ${level}`,
      restore,
      writes,
      ctrl.id,
      `${ctrl.label}: ${from} → ${after ?? level} · moved ${moved} setting${moved === 1 ? "" : "s"}`,
    );
    return this.lastStep;
  }

  /** What a write would replace. */
  private current(w: Write): LookValue {
    return w.groupId ? this.readPart(w.key, w.groupId) : this.readRow(w.key);
  }

  /**
   * Of the values an Undo puts back after re-applying a part's material, the
   * ones the part does not already hold. The re-applied preset usually puts
   * them back itself, exactly; writing them again can only lose precision — a
   * stone colour goes through 8-bit hex, clamped to 0–1, so an emerald's
   * 1.15 green came back as 1.0 (measured, Preview as customer).
   */
  private stillDifferent(values: Write[]): Write[] {
    return values.filter((v) => v.value !== null && !sameValue(this.current(v), v.value));
  }

  /**
   * Set fields to values (swatches, toggles, AI Look Match). Every value is
   * sanitised against its look field first — this is the browser-side half of
   * "never trust a proposed value".
   */
  setValues(changes: LookChange[], label: string, scope: BasicScope = ALL_PARTS, controlId?: string): Promise<AppliedStep | null> {
    return this.enqueue(async () => {
      const clean: LookChange[] = [];
      for (const c of changes) {
        const f = lookField(c.key);
        if (!f || !LOOK_TARGETS[c.key] || !this.available(c.key)) continue;
        const v = f.type === "color" && c.value === null && c.key === "light.background" ? null : sanitizeLookValue(f, c.value);
        if (v === undefined) continue;
        clean.push({ key: c.key, value: typeof v === "number" ? clampQuant(c.key, v) : v });
      }
      const writes = this.expand(clean, scope);
      if (writes.length === 0) return null;
      const restore = await this.applyWrites(writes);
      this.record(label, restore, writes, controlId);
      return this.lastStep;
    });
  }

  // ── clarity ─────────────────────────────────────────────────────────────

  /** Write one inclusion state per stone, then re-read what the viewer kept. */
  private async putInclusions(ids: string[], payloads: GemInclusionParams[]) {
    try {
      await Promise.all(ids.map((gid, i) => this.ring.setGroupGemInclusions(gid, payloads[i])));
    } finally {
      await this.readInclusions(ids);
    }
  }

  /** The stones whose inclusions Basic changed and that still differ from how
   *  they loaded, with both states — for Reset. */
  private async clarityDrift(): Promise<{ ids: string[]; now: GemInclusionParams[]; then: GemInclusionParams[] } | null> {
    if (!this.clarityOn || this.inclusionBaseline.size === 0) return null;
    const ids = [...this.inclusionBaseline.keys()].filter((id) => this.groups("gem").some((g) => g.id === id));
    const now = await Promise.all(ids.map((id) => this.ring.getGroupGemInclusions(id).catch(() => null)));
    const drift = ids
      .map((id, i) => ({ id, now: inclusionPayload(now[i]), then: inclusionPayload(this.inclusionBaseline.get(id) ?? null) }))
      // Two "off" states are the same stone whatever maps they still name.
      .filter((d) => (d.now.enabled || d.then.enabled) && JSON.stringify(d.now) !== JSON.stringify(d.then));
    if (drift.length === 0) return null;
    return { ids: drift.map((d) => d.id), now: drift.map((d) => d.now), then: drift.map((d) => d.then) };
  }

  /**
   * The clarity grade of the stones in scope: a grade id, null when they
   * carry inclusions no grade describes (or differ from each other), and
   * undefined until the state has been read.
   */
  clarity(scope: BasicScope = ALL_PARTS): ClarityId | null | undefined {
    const ids = this.scopeGroups("gem", scope).map((g) => g.id);
    if (ids.length === 0 || ids.some((id) => !this.inclusions.has(id))) return undefined;
    const grades = new Set(ids.map((id) => clarityOfState(this.inclusions.get(id) ?? null)));
    return grades.size === 1 ? [...grades][0] : null;
  }

  /**
   * Give every stone in scope a clarity grade, as ONE Undo step. Undo writes
   * back each stone's own inclusion state as the viewer reported it just
   * before — maps, strengths and all — not a guess.
   */
  setClarity(id: ClarityId, scope: BasicScope = ALL_PARTS): Promise<AppliedStep | null> {
    return this.enqueue(async () => {
      const grade = CLARITY_GRADES.find((g) => g.id === id);
      if (!grade || !this.clarityOn) return null;
      const ids = this.scopeGroups("gem", scope).map((g) => g.id);
      if (ids.length === 0) return null;
      const before = await Promise.all(ids.map((gid) => this.ring.getGroupGemInclusions(gid).catch(() => null)));
      ids.forEach((gid, i) => {
        if (!this.inclusionBaseline.has(gid)) this.inclusionBaseline.set(gid, before[i]);
      });
      const doIt = () => this.putInclusions(ids, ids.map(() => grade.params));
      const undoIt = () => this.putInclusions(ids, before.map(inclusionPayload));
      try {
        await doIt();
      } catch (e) {
        // A map that failed to load leaves the stones half-changed: put them
        // back as they were before saying so.
        await undoIt().catch(() => undefined);
        throw e;
      }
      const label = `Clarity: ${grade.label}`;
      this.push({ label, restore: [], applied: [], custom: { undo: undoIt, redo: doIt } });
      const what = scope.gem === "all" ? "all stones" : "this part";
      this.lastStep = { label, summary: `${label} · ${what}`, caption: "", rows: [CLARITY_ROW], at: Date.now(), controlId: "clarity" };
      return this.lastStep;
    });
  }

  undo(): Promise<AppliedStep | null> {
    return this.enqueue(async () => {
      const entry = this.undoStack.pop();
      if (!entry) return null;
      if (entry.custom) await entry.custom.undo();
      else await this.applyWrites(entry.restore);
      this.redoStack.push(entry);
      this.lastStep = { label: `Undid “${entry.label}”`, summary: `Undid “${entry.label}”`, caption: "", rows: [], at: Date.now() };
      return this.lastStep;
    });
  }

  redo(): Promise<AppliedStep | null> {
    return this.enqueue(async () => {
      const entry = this.redoStack.pop();
      if (!entry) return null;
      if (entry.custom) await entry.custom.redo();
      else await this.applyWrites(entry.applied);
      this.undoStack.push(entry);
      this.lastStep = { label: `Redid “${entry.label}”`, summary: `Redid “${entry.label}”`, caption: "", rows: [], at: Date.now() };
      return this.lastStep;
    });
  }

  /** Writes that bring the scene to `snap` (skipping what already matches). */
  private writesTo(snap: Map<string, LookValue>, category?: LookCategory): Write[] {
    const writes: Write[] = [];
    for (const [k, v] of snap) {
      const [key, groupId] = k.split("@");
      if (category && lookField(key)?.category !== category) continue;
      if (v === null && key !== "light.background") continue;
      if (groupId && !this.mats.has(groupId)) continue;
      const cur = groupId ? this.readPart(key, groupId) : this.readRow(key);
      if (cur === v || (typeof cur === "number" && typeof v === "number" && Math.abs(cur - v) < 1e-9)) continue;
      writes.push({ key, value: v, groupId });
    }
    return writes;
  }

  /**
   * Compare, pressed: show the model as it loaded. Not an Undo step — the
   * values in force are held and `endPreview` puts them back exactly.
   */
  preview(): Promise<boolean> {
    return this.enqueue(async () => {
      if (!this.baseline || this.previewSnap) return false;
      this.previewSnap = this.snapshotAll();
      await this.applyWrites(this.writesTo(this.baseline));
      return true;
    });
  }

  /** Compare, released. */
  endPreview(): Promise<void> {
    return this.enqueue(async () => {
      const snap = this.previewSnap;
      if (!snap) return;
      this.previewSnap = null;
      await this.applyWrites(this.writesTo(snap));
    });
  }

  // ── named materials ─────────────────────────────────────────────────────

  /** The named metal whose colour is nearest `hex` — for an unknown "before". */
  private nearestMetal(hex: string | null): string {
    const ids = Object.keys(this.metalColors);
    if (!hex || ids.length === 0) return ids[0] ?? "whiteGold";
    const [r, g, b] = fromHex(hex).map((x) => x * 255);
    let best = ids[0];
    let bestD = Infinity;
    for (const id of ids) {
      const c = this.metalColors[id];
      const d = Math.hypot(r - ((c >> 16) & 255), g - ((c >> 8) & 255), b - (c & 255));
      if (d < bestD) { bestD = d; best = id; }
    }
    return best;
  }

  private kindFields(kind: MaterialKind): string[] {
    return LOOK_FIELDS.filter((f) => MATERIAL_KIND[this.target(f.key).kind] === kind).map((f) => f.key);
  }

  /** Re-read the listed parts' params (a preset rewrites all of them). */
  private async reloadParts(ids: string[]) {
    const [results, exts] = await Promise.all([
      Promise.all(ids.map((id) => this.ring.getGroupMaterialParams(id).catch(() => null))),
      Promise.all(ids.map((id) => this.ring.getGroupMaterialExtensions(id).catch(() => null))),
    ]);
    ids.forEach((id, i) => {
      const r = results[i];
      if (r) this.mats.set(id, r);
      const e = exts[i];
      if (e) this.exts.set(id, e);
      else this.exts.delete(id);
    });
    // A gem material file replaces a stone's inclusions.
    if (this.clarityOn) {
      const gems = ids.filter((id) => this.mats.get(id)?.kind === "gem");
      if (gems.length) await this.readInclusions(gems);
    }
    const selected = this.store.getState("selection") as string | null;
    if (selected && ids.includes(selected)) await this.store.refresh("material");
  }

  /** Is this file mark a diamond (.dmat, or a .pmat that declared itself one)? */
  private isDmatMark(mark: PresetMark, kind: MaterialKind): boolean {
    return isDmatMark(mark, kind);
  }

  /** The kind of part a mark leaves behind (`./markKind.ts`, shared with the configurator's picker). */
  private markKind(mark: PresetMark, fallback: MaterialKind): MaterialKind {
    return markKind(mark, fallback);
  }

  /** The one RPC that puts `mark` on a part of `kind`. */
  private async putPreset(kind: MaterialKind, id: string, mark: PresetMark) {
    if (mark.url) {
      if (this.isDmatMark(mark, kind)) await this.ring.applyDmatUrlToGroup(id, mark.url);
      else await this.ring.applyPmatUrlToGroup(id, mark.url);
    } else if (mark.category && mark.category !== "metal" && mark.category !== "gem") {
      // Ceramic / pearl / enamel ids exist only in the material library. Built-in
      // METAL and GEM library ids are the named presets above (applyMaterialPresetToGroup
      // just forwards them, and would reset a metal's finish to polished on the way).
      await this.ring.applyMaterialPresetToGroup(id, mark.presetId);
    } else if (kind === "metal") {
      await this.ring.applyMetalPresetToGroup(id, mark.presetId, mark.finishId ?? "polished");
    } else {
      await this.ring.applyGemPresetToGroup(id, mark.presetId);
    }
    this.presetOf.set(id, mark);
  }

  /**
   * Put `mark` on a part that is `kindNow`, leaving it a `kind` part.
   *
   * ⚠️ A gem FILE never builds a stone: on a part that already has the diamond
   * shader, applyDmatUrlToGroup only sets its uniforms, and on any other part
   * the viewer swaps in a PBR transmission stand-in — glass pretending to be a
   * diamond, which the studio never ships. So a part becoming a stone through a
   * file gets the real shader (a clear diamond) first, inside the same step.
   */
  private async putMaterial(kind: MaterialKind, id: string, mark: PresetMark, kindNow: MaterialKind) {
    if (kind !== kindNow) await this.ring.reclassifyGroup(id, kind);
    if (kind === "gem" && kindNow !== "gem" && mark.url) await this.ring.applyGemPresetToGroup(id, "diamondClear");
    await this.putPreset(kind, id, mark);
  }

  /** What a part goes back to when we never applied anything to it: the
   *  nearest named metal, or a clear diamond. */
  private fallbackMark(kind: MaterialKind, values: Write[]): PresetMark {
    return kind === "metal"
      ? { presetId: this.nearestMetal(values.find((v) => v.key === "metal.color")?.value as string | null), finishId: "polished" }
      : { presetId: "diamondClear" };
  }

  /** Each part's kind as the viewer last reported it. */
  private kindsOf(ids: string[]): Map<string, MaterialKind> {
    const all = (this.store.getState("groups") as GroupInfo[] | undefined) ?? [];
    return new Map(all.filter((g) => ids.includes(g.id)).map((g) => [g.id, g.kind]));
  }

  /**
   * Apply a named material (Yellow Gold · Hammered, Ruby…) to every part of
   * that kind in scope, as ONE Undo step. The viewer cannot report which
   * preset a part had, so Undo re-applies the one we applied before if there
   * was one, otherwise the nearest named metal (or a clear diamond), and then
   * writes every look value back exactly.
   */
  applyPreset(kind: MaterialKind, mark: PresetMark, label: string, scope: BasicScope = ALL_PARTS, controlId?: string): Promise<AppliedStep | null> {
    return this.enqueue(async () => {
      const ids = this.scopeGroups(kind, scope).map((g) => g.id);
      if (ids.length === 0) return null;
      const fields = this.kindFields(kind);
      const before = ids.map((id) => ({
        id,
        mark: this.presetOf.get(id),
        values: fields.map((key) => ({ key, value: this.readPart(key, id), groupId: id })),
      }));
      const doIt = async () => {
        for (const id of ids) await this.putPreset(kind, id, mark);
        await this.reloadParts(ids);
      };
      const undoIt = async () => {
        for (const b of before) {
          // putMaterial, not putPreset: the mark being restored may be a gem FILE.
          await this.putMaterial(kind, b.id, b.mark ?? this.fallbackMark(kind, b.values), kind);
          if (!b.mark) this.presetOf.delete(b.id);
        }
        await this.reloadParts(ids);
        await this.applyWrites(this.stillDifferent(before.flatMap((b) => b.values)));
      };
      await doIt();
      this.push({ label, restore: [], applied: [], custom: { undo: undoIt, redo: doIt } });
      const what = scope[kind] === "all" ? (kind === "metal" ? "all metal" : "all stones") : "this part";
      this.lastStep = { label, summary: `${label} · ${what}`, caption: "", rows: [], at: Date.now(), controlId };
      return this.lastStep;
    });
  }

  /**
   * Apply a LIBRARY material — a built-in, a curated .pmat/.dmat, or one the
   * workspace added — to the listed parts, as ONE Undo step.
   *
   * It takes part ids rather than a kind + scope because the library panels
   * reach across kinds (Other offers every part; Pearls offers the stones plus
   * the parts already wearing a pearl), and because a material can CHANGE a
   * part's kind: a ceramic or a .pmat leaves it a PBR (metal) part, a .dmat
   * leaves it a stone. The ids are resolved when the user clicks, so a second
   * click lands on the parts they saw, not on whatever the first one re-filed.
   *
   * Undo puts each part back as the kind it was, re-applies the mark we gave it
   * before (or the nearest named metal / a clear diamond), then writes every
   * look value of that kind back exactly — the same contract as `applyPreset`.
   */
  applyLibraryMaterial(parts: string[], mark: PresetMark, label: string, controlId?: string): Promise<AppliedStep | null> {
    return this.enqueue(async () => {
      const kinds = this.kindsOf(parts);
      const ids = parts.filter((id) => kinds.has(id));
      if (ids.length === 0) return null;
      const before = ids.map((id) => {
        const kind = kinds.get(id) as MaterialKind;
        return {
          id,
          kind,
          mark: this.presetOf.get(id),
          values: this.kindFields(kind).map((key) => ({ key, value: this.readPart(key, id), groupId: id }) as Write),
        };
      });
      let applied = mark;
      const doIt = async () => {
        const now = this.kindsOf(ids);
        for (const b of before) {
          const was = now.get(b.id) ?? b.kind;
          await this.putMaterial(this.markKind(applied, was), b.id, applied, was);
        }
        await this.store.refresh("groups");
        // A .pmat can DECLARE itself a DiamondMaterial, and the viewer applies
        // it as one whatever the extension says — reporting the part as a stone
        // afterwards. On a part that was already a stone that only poked its
        // uniforms; on a metal part it built the PBR stand-in putMaterial
        // exists to prevent. Give those the real shader and re-apply, and
        // remember the file is a diamond so Redo/Undo route it right first time.
        if (applied.url && !this.isDmatMark(applied, "metal")) {
          const after = this.kindsOf(ids);
          const turned = ids.filter((id) => after.get(id) === "gem");
          if (turned.length > 0) {
            applied = { ...applied, dmat: true };
            for (const id of turned) {
              if (now.get(id) !== "gem") {
                await this.ring.applyGemPresetToGroup(id, "diamondClear");
                await this.ring.applyDmatUrlToGroup(id, applied.url as string);
              }
              // Refreshes the viewer's reflection groups, which a file apply does not.
              await this.ring.reclassifyGroup(id, "gem");
            }
            for (const id of ids) this.presetOf.set(id, applied);
            await this.store.refresh("groups");
          }
        }
        await this.reloadParts(ids);
      };
      const undoIt = async () => {
        const now = this.kindsOf(ids);
        for (const b of before) {
          await this.putMaterial(b.kind, b.id, b.mark ?? this.fallbackMark(b.kind, b.values), now.get(b.id) ?? b.kind);
          if (!b.mark) this.presetOf.delete(b.id);
        }
        await this.store.refresh("groups");
        await this.reloadParts(ids);
        await this.applyWrites(this.stillDifferent(before.flatMap((b) => b.values)));
      };
      await doIt();
      this.push({ label, restore: [], applied: [], custom: { undo: undoIt, redo: doIt } });
      const what = ids.length === 1 ? "1 part" : `${ids.length} parts`;
      this.lastStep = { label, summary: `${label} · ${what}`, caption: "", rows: [], at: Date.now(), controlId };
      return this.lastStep;
    });
  }

  /**
   * Apply accepted materials-check suggestions — kind fixes and named
   * materials — as ONE Undo step.
   */
  applySuggestions(
    items: { groupId: string; kind: MaterialKind; changeKind: boolean; mark: PresetMark }[],
    label: string,
  ): Promise<AppliedStep | null> {
    return this.enqueue(async () => {
      if (items.length === 0) return null;
      const groupsNow = (this.store.getState("groups") as GroupInfo[] | undefined) ?? [];
      const before = items.map((it) => {
        const was = groupsNow.find((g) => g.id === it.groupId)?.kind ?? it.kind;
        return {
          ...it,
          wasKind: was,
          wasMark: this.presetOf.get(it.groupId),
          values: this.kindFields(was).map((key) => ({ key, value: this.readPart(key, it.groupId), groupId: it.groupId })),
        };
      });
      const ids = items.map((i) => i.groupId);
      const doIt = async () => {
        for (const it of items) {
          if (it.changeKind) await this.ring.reclassifyGroup(it.groupId, it.kind);
          await this.putPreset(it.kind, it.groupId, it.mark);
        }
        await this.store.refresh("groups");
        await this.reloadParts(ids);
      };
      const undoIt = async () => {
        for (const b of before) {
          // The step left the part `b.kind` when it changed kind, else as it was.
          // putMaterial reclassifies back and, if the restored mark is a gem
          // FILE, rebuilds the diamond shader it needs to land on.
          await this.putMaterial(b.wasKind, b.groupId, b.wasMark ?? this.fallbackMark(b.wasKind, b.values), b.changeKind ? b.kind : b.wasKind);
          if (!b.wasMark) this.presetOf.delete(b.groupId);
        }
        await this.store.refresh("groups");
        await this.reloadParts(ids);
        await this.applyWrites(this.stillDifferent(before.flatMap((b) => b.values)));
      };
      await doIt();
      this.push({ label, restore: [], applied: [], custom: { undo: undoIt, redo: doIt } });
      this.lastStep = { label, summary: label, caption: "", rows: [], at: Date.now() };
      return this.lastStep;
    });
  }

  /** Write the pre-tuning snapshot back, for one category or all (clarity
   *  included with the diamond and with everything). Undoable, one step. */
  reset(category?: LookCategory): Promise<AppliedStep | null> {
    return this.enqueue(async () => {
      const writes = this.baseline ? this.writesTo(this.baseline, category) : [];
      return this.resetTo(writes, category ? "Reset" : "Reset all", !category || category === "diamond");
    });
  }

  /**
   * Write the pre-tuning snapshot back for the listed fields only — a
   * Fine-tune card's Reset, which puts back what that card's controls show
   * (the Diamond card's Sparkle is the glow, a light field) and, for the
   * Diamond card, the stones' clarity. Undoable, one step.
   */
  resetFields(keys: string[], label = "Reset", withClarity = false): Promise<AppliedStep | null> {
    return this.enqueue(async () => {
      const want = new Set(keys);
      const writes = this.baseline ? this.writesTo(this.baseline).filter((w) => want.has(w.key)) : [];
      return this.resetTo(writes, label, withClarity);
    });
  }

  /** Inside the queue: apply a reset's writes and, optionally, the clarity it
   *  owes, recorded as ONE Undo step. */
  private async resetTo(writes: Write[], label: string, withClarity: boolean): Promise<AppliedStep | null> {
    const drift = withClarity ? await this.clarityDrift() : null;
    if (writes.length === 0 && !drift) return null;
    const restore = writes.length ? await this.applyWrites(writes) : [];
    if (!drift) {
      this.record(label, restore, writes);
      return this.lastStep;
    }
    await this.putInclusions(drift.ids, drift.then);
    this.push({
      label,
      restore: [],
      applied: [],
      custom: {
        undo: async () => {
          if (restore.length) await this.applyWrites(restore);
          await this.putInclusions(drift.ids, drift.now);
        },
        redo: async () => {
          if (writes.length) await this.applyWrites(writes);
          await this.putInclusions(drift.ids, drift.then);
        },
      },
    });
    const { caption, rows } = this.caption(restore, writes);
    const moved = new Set(writes.map((w) => w.key)).size;
    const what = moved ? `clarity and ${moved} setting${moved === 1 ? "" : "s"}` : "clarity";
    this.lastStep = { label, summary: `${label} · ${what}`, caption, rows, at: Date.now() };
    return this.lastStep;
  }

  /** Every look field's current value — what the AI is told about the scene. */
  currentValues(categories?: LookCategory[]): Record<string, LookValue> {
    const out: Record<string, LookValue> = {};
    for (const f of LOOK_FIELDS) {
      if (categories && !categories.includes(f.category)) continue;
      if (!this.available(f.key)) continue;
      const v = this.read(f.key);
      if (v !== null) out[f.key] = v;
    }
    return out;
  }
}

const engines = new WeakMap<EditorStore, BasicEngine>();

/** One engine per editor store, shared by the Basic and AI panels. */
export function basicEngineFor(store: EditorStore): BasicEngine {
  let e = engines.get(store);
  if (!e) {
    e = new BasicEngine(store);
    engines.set(store, e);
  }
  return e;
}
